import { gql, NetworkStatus, useQuery } from '@apollo/client'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistCollectionsConnectionFragmentDoc,
  CollectionsSelectBoxFragmentDoc,
  ConsoleCollectionsQuery,
  ConsoleCollectionsQueryVariables,
  OrderBy,
} from '@/clients/utoniq-core/schema'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const params = new URLSearchParams(window.location.search)
  const sort = params.get('sort') === OrderBy.Asc ? OrderBy.Asc : OrderBy.Desc
  const { isFirstLoadRef } = useGetNewestData<
    ConsoleCollectionsQuery,
    ConsoleCollectionsQueryVariables
  >({
    query: FETCH_NEW_COLLECTION,
    variables: { orderBy: sort },
    fields: [
      {
        fragmentName: 'CollectionsSelectBox',
        fieldName: 'artistCollections',
        fragment: CollectionsSelectBoxFragmentDoc,
      },
    ],
  })

  const { data, loading, fetchMore, refetch, networkStatus } = useQuery<
    ConsoleCollectionsQuery,
    ConsoleCollectionsQueryVariables
  >(QUERY, {
    variables: { orderBy: sort },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  const isNextFetch = !!data

  return {
    state: {
      artistId: user?.uid ?? '',
      loading: loading && !isNextFetch,
      refetching: loading || networkStatus === NetworkStatus.fetchMore,
      hasNextPage: !!data?.artistCollections?.pageInfo.hasNextPage,
      collections: data?.artistCollections ?? null,
      sort: sort,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            pageInput: {
              cursor: data?.artistCollections?.pageInfo.endCursor || null,
            },
          },
        })
      },
      async onSort(value) {
        await refetch({ orderBy: value })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleCollections($pageInput: PageInputDate, $orderBy: OrderBy) {
    artistCollections(pageInput: $pageInput, orderBy: $orderBy) {
      ...ArtistCollectionsConnection
    }
  }
  ${ArtistCollectionsConnectionFragmentDoc}
`

const FETCH_NEW_COLLECTION = gql`
  query FetchNewConsoleCollections(
    $pageInput: PageInputDate
    $orderBy: OrderBy
  ) {
    artistCollections(pageInput: $pageInput, orderBy: $orderBy) {
      ...ArtistCollectionsConnection
    }
  }
  ${ArtistCollectionsConnectionFragmentDoc}
`
